import {Injectable} from '@angular/core';
import {HttpClient, HttpHeaders} from '@angular/common/http';

import {environment} from 'src/environments/environment';
import { searchCollections } from '../classes/search-collections';
import { IMAGE_TYPE_NONE, imageTypeCode } from '../classes/image-type';
import {CartData, CartStatus, Contact, ContributorData, CreditPack, Order, Plan, Results, Subscription, User, WishList, Data} from '../classes/app.models';
import { forkJoin, Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';

declare let $: any;

@Injectable({
    providedIn: 'root'
})

export class ApiService {
    public Data = new Data(
        [],
        0,
        0
    );
    searchHintUrl = environment.searchhint;
    backendUrl = environment.backendUrl;
    exclusiveBackendUrl = environment.exclusiveBackendUrl;
    contributorUrl = environment.contributorUrl;
    siteadminUrl = environment.siteadminUrl;
    user = null;

    constructor(private http: HttpClient) {

    }

    /**
     * The creative library search, called straight against the search service
     * rather than through a backend proxy.
     *
     * Deliberately separate from getImages(): that one picks its endpoint from the
     * signed-in user's license_type and forks across two services for a visitor,
     * which is right for the search pages. The studio's stock panel always wants
     * the one IndiaPicture index, so it asks for it directly.
     *
     * Returns the service's own shape — { total, pages, results, time } — untouched.
     *
     * @param query      what to search for, unencoded
     * @param perPage    rows wanted
     * @param page       1-based
     * @param collId     comma-joined collection ids; '' falls back to the
     *                   site-wide list (see searchCollections)
     * @param customerId used by the service for entitlement-aware pricing
     * @param skipLoader true to keep a keystroke off the global loading bar
     */
    public searchStockImages(
        query: string,
        perPage: number,
        page: number,
        collId: string = '',
        customerId: string = '',
        skipLoader = true
    ): Observable<any> {
        const text = encodeURIComponent(query || '');

        // Defaulted, so the studio's stock search covers the same library as the
        // exclusive creativeSearch/editorialSearch rather than everything.
        const collections = searchCollections(collId);

        // searchtext and advsearchtxt carry the same words: the service reads one or
        // the other depending on which index answers.
        const qs = [
            `searchtext=${text}`,
            `advsearchtxt=${text}`,
            'internationalCollection=',
            `page=${page}`,
            `images=${perPage}`,
            `coll_id=${collections}`,
            `customerId=${customerId || ''}`,
            // The studio's stock search has no image-type filter.
            `imageType=${IMAGE_TYPE_NONE}`
        ].join('&');

        /*
         * Query params only — no custom headers.
         *
         * The search service answers a plain cross-origin GET but not a preflight
         * OPTIONS, and any header outside the CORS safelist (`ignoreLoadingBar` was
         * the culprit) makes the browser send one first, which fails before the GET
         * is ever attempted. AppInterceptor reads skipLoader from the params
         * anyway, so the header bought nothing.
         */
        const params: any = {};
        if (skipLoader) { params.skipLoader = 'true'; }

        return this.http.get(`${environment.searchIndiapictureApiUrl}?${qs}`, { params });
    }

    /**
     * Preloader helpers — usable from any component/service.
     */
    public showPreloader(): void {
        if (typeof $ !== 'undefined') {
            $('.preloader').stop(true, true).fadeIn('slow');
        }
    }

    public hidePreloader(): void {
        if (typeof $ !== 'undefined') {
            $('.preloader').stop(true, true).hide();
        }
    }

    /**
     * Get Products
     */
    public fetchShopData(params: any, perPage: number, initial = 'shop'): Observable<any> {
        let temp = initial;
        if (!initial.includes('?')) {
            temp += '?';
        }

        for (let key in params) {
            temp += key + '=' + params[key] + '&';
        }

        if (!params.page) {
            temp += 'page=1';
        }

        if (!params.perPage) {
            temp += '&images=' + perPage;
        }

        temp += '&demo=' + environment.demo;

        return this.http.get(`${environment.SERVER_URL}/${temp}`);
    }

    /**
     * Get Products
     */
    public fetchBlogData(params: any, initial = 'blogs/classic', perPage: number,): Observable<any> {
        let temp = initial;
        if (!initial.includes('?')) {
            temp += '?';
        }

        for (let key in params) {
            temp += key + '=' + params[key] + '&';
        }

        if (!params.page) {
            temp += 'page=1';
        }

        if (!params.perPage) {
            temp += '&perPage=' + perPage;
        }

        return this.http.get(`${environment.SERVER_URL}/${temp}`);
    }

    /**
     * Get Products
     */
    public fetchSinglePost(slug: string): Observable<any> {
        return this.http.get(`${environment.SERVER_URL}/${'single/' + slug + '?demo=' + environment.demo}`);
    }

    /**
     * Get Products for home page
     */
    public fetchHomeData(): Observable<any> {
        return this.http.get(`${environment.SERVER_URL}/${environment.demo}`);
    }

    /**
     * Get products by demo
     */
    public getSingleProduct(slug: string, isQuickView = false): Observable<any> {
        return this.http.get(`${environment.SERVER_URL}/products/${slug}?demo=${environment.demo}&isQuickView=${isQuickView}`);
    }

    /**
     * Get Products
     */
    // public fetchHeaderSearchData(searchTerm: string, cat = null): Observable<any> {
    // 	return this.http.get(`${environment.SERVER_URL}/shop?perPage=5&searchTerm=${searchTerm}&category=${cat}&demo=${environment.demo}`);
    // }

    public getSearchHint(text): Observable<any> {
        let options = {};
        const skipLoader = true;
        if (skipLoader) {
            options = {
                params: {skipLoader},
                headers: {ignoreLoadingBar: skipLoader}
            };
        }
        return this.http.get(this.searchHintUrl + '?searchText=' + text, options);
    }

    /**
     * Get Element Products
     */
    public fetchElementData(): Observable<any> {
        return this.http.get(`${environment.SERVER_URL}/elements/products`);
    }

    /**
     * Get Element Blog
     */
    public fetchElementBlog(): Observable<any> {
        return this.http.get(`${environment.SERVER_URL}/elements/blogs`);
    }


    //  TEMPORARY UI HIDE – remove later
    private tempHideCollections(results: any[], customerId: string): any[] {
      const TEMP_HIDE_CUSTOMER_ID = 'IND243742454';
        const TEMP_HIDDEN_COLLECTIONS = [93, 13];

        if (customerId !== TEMP_HIDE_CUSTOMER_ID) {
            return results;
        }

        return results.filter(
            item => !TEMP_HIDDEN_COLLECTIONS.includes(Number(item.collection))
        );
    }




    /* Custom Api */
    public fetchData(params: any, perPage: number, type: string, customerId: string, skipLoader = false, initial = ''): Observable<any> {
        let category = 'creative';
        let durationExist = false;
        let fpsExist = false;

        // Retrieve currentUser from localStorage
        const currentUser = JSON.parse(localStorage.getItem('currentUser'));

        // ADD (safe customer id resolution)
        const customerIdFromStorage = currentUser?.customers_id || customerId;

        // Initialize URL
        let url: string | null = null;

        // Check license type if user exists
        if (currentUser && currentUser.license_type) {
            const licenseType = currentUser.license_type;

            if (licenseType === 'standard_yearly' || licenseType === 'standard_monthly') {
            url = environment.searchIndiapictureBudgetApiurl;
            } else if (licenseType === 'standard_subscription' || licenseType === 'enhanced') {
            url = environment.searchIndiapictureApiUrl;
            }
        }

        // Build base URL string
        let temp = initial;
        if (!initial.includes('?')) temp += '?';

        // Build query parameters
        for (let key in params) {
            if (key === 'searchText' && type === 'images') {
            temp += `searchtext=${params[key]}&advsearchtxt=${params[key]}&`;
            } else if (key === 'category') {
            category = params[key];
            } else if (key === 'durationFrom') {
            durationExist = true;
            } else if (key === 'fpsFrom') {
            fpsExist = true;
            } else if (key === 'imageType') {
            // Derived from the sidebar's `type` below; never passed through raw.
            } else {
            temp += `${key}=${params[key]}&`;
            }
        }

        if (!params.page) temp += 'page=1';
        if (!params.perPage) {
            if (type === 'images') {
                  // The plan's own collections when the customer has them, the
                  // site-wide list when they do not — signed out, this used to
                  // send an empty coll_id and search everything.
                  const planCollectionIds = searchCollections(currentUser?.plan_collections);
            temp += category === 'creative' ? `&images=${perPage}&coll_id=${planCollectionIds}` : `&noimages=${perPage}&coll_id=${planCollectionIds}`;
            // temp += category === 'creative' ? `&images=${perPage}` : `&noimages=${perPage}`;
            } else {
            temp += `&no=${perPage}`;
            }
        }

        // Handle collections
        let collId = '';
        if (params.indianCollection) collId += params.indianCollection;
        if (params.internationalCollection) 
            collId += (collId !== '' ? ',' : '') + params.internationalCollection;

        temp += `&customerId=${customerId}`;

        // search-exclusive-new.php's image-type code: vector 8, illustration 2,
        // photo or no choice null. Videos go to the backend, which has no such key.
        if (type === 'images') {
            temp += `&imageType=${imageTypeCode(params.type)}`;
        }

        if (category === 'editorial') {
            url = environment.searchEdiApiUrl || environment.searchIndiapictureApiUrl;
        }

        if (type === 'videos') {
            url = environment.backendUrl + 'auth/videoSearch';
            temp += category === 'editorial' ? '&editorial=1' : '&editorial=0';
            if (!durationExist) temp += '&durationFrom=0&durationTo=120';
            if (!fpsExist) temp += '&fpsFrom=0&fpsTo=60';
            temp += '&priceFrom=0&priceTo=10000';
            collId = params.videoCollection || '';
        }

        if (collId) temp += `&coll_id=${collId}`;

        // Loader options
        let options: any = {};
        if (skipLoader) {
            options = {
            params: { skipLoader },
            headers: { ignoreLoadingBar: skipLoader },
            };
        }

        // --- 🔹 Case 1: If user exists & URL decided from license type ---
        if (url) {
            // console.log('Using single URL →', url + temp);

            //  return this.http.get(url + `${temp}`, options); //old code working
            return this.http.get(url + `${temp}`, options).pipe(
            map((res: any) => ({
                ...res,
                // ADD (temporary UI filter)
                results: this.tempHideCollections(
                res?.results || [],
                customerIdFromStorage
                )
            }))
            );
        }

                // --- 🔹 Case 2: No user or no license_type → call both APIs and combine results ---
             //  console.log('No user found. Calling both APIs...');
        const api1 = environment.searchIndiapictureApiUrl;
        const api2 = environment.searchIndiapictureBudgetApiurl;

        /*
         * ONE call when both keys name the same index.
         *
         * The merge below exists to blend two DIFFERENT indexes: it takes half a
         * page from each and adds the two totals together. Point both keys at
         * one URL — which is what routing search to search-exclusive-new.php
         * does — and the two responses are identical, so a signed-out visitor
         * saw every result twice and a total of roughly double the real figure.
         *
         * Compared rather than special-cased on the current URL, so this holds
         * whenever the two are configured the same, including after the
         * temporary reroute is reverted.
         */
        if (api1 === api2) {
            return this.http.get(api1 + `${temp}`, options).pipe(
                map((res: any) => ({
                    total: res?.total || 0,
                    pages: res?.pages || 0,
                    perPage: perPage,
                    results: this.tempHideCollections(res?.results || [], customerIdFromStorage)
                }))
            );
        }

        return forkJoin([
            this.http.get(api1 + `${temp}`, options),
            this.http.get(api2 + `${temp}`, options),
        ]).pipe(
            map(([res1, res2]: any) => {
            const data1 = res1?.results || [];
            const data2 = res2?.results || [];

                        // mix based on perPage
            const half = Math.floor(perPage / 2);

            const mix1 = data1.slice(0, half);
            const mix2 = data2.slice(0, half);

            const combinedResults = [...mix1, ...mix2];

            // ADD (temporary UI filter)
            const filteredResults = this.tempHideCollections(
                combinedResults,
                customerIdFromStorage
            );

            const total = (res1?.total || 0) + (res2?.total || 0);
            const pages = (res1?.pages || 0) + (res2?.pages || 0);

            return {
                total: total,
                pages: pages,
                perPage: perPage,
                // results: combinedResults //old code wokring
                results: filteredResults
            };
            })
        );
    }

    /*
     * `recaptchaToken` is the reCAPTCHA v3 token for this submission, verified
     * server-side before the credentials are even looked at. Optional in the
     * signature so the older two-argument calls still compile, but the backend
     * rejects an empty one while RECAPTCHA_ENABLED is on — AuthenticationService
     * is the caller that supplies it.
     */
    public signIn(email, password, recaptchaToken = ''): Observable<any> {
        return this.http.post<any>(this.backendUrl + 'login', {email, password, recaptcha_token: recaptchaToken});
    }

    // public signIn(email, password): Observable<any> {
    //     return this.http.post<any>(this.backendUrl + 'auth/login', {email, password});
    // }

     public exclusiveSignIn(email, password): Observable<any> {
     //  console.log('exclusiveSignIn ', email, password)
        return this.http.post<any>(this.exclusiveBackendUrl + 'auth/login', {email, password});
    }

    /* reCAPTCHA token as in signIn(). */
    public register(name, email, phone, password, recaptchaToken = ''): Observable<any> {
        return this.http.post<any>(this.backendUrl + 'auth/register', {name, email, phone, password, recaptcha_token: recaptchaToken});
    }

    /*
     * Email OTP sign-up / sign-in.
     *
     * The two verify endpoints answer in the same shape as signIn() -- the
     * customer row under `user.data` with `user.msg === 'success'`, plus `cart` --
     * so AuthenticationService reuses one session path for all three.
     *
     * All four are throttled server-side (send 5/min, verify 10/min) and report
     * failures as non-2xx with a `message`, so callers must handle the error arm.
     *
     * The two SEND calls also carry a reCAPTCHA token — they are what the login
     * and register forms actually submit when OTP is the chosen mode. The verify
     * calls do not; see the note on the routes.
     */
    public sendRegisterEmailOtp(email, recaptchaToken = ''): Observable<any> {
        return this.http.post<any>(this.backendUrl + 'auth/email/register/send-otp', {email, recaptcha_token: recaptchaToken});
    }

    public verifyRegisterEmailOtp(email, otp): Observable<any> {
        return this.http.post<any>(this.backendUrl + 'auth/email/register/verify-otp', {email, otp});
    }

    public sendLoginEmailOtp(email, recaptchaToken = ''): Observable<any> {
        return this.http.post<any>(this.backendUrl + 'auth/email/login/send-otp', {email, recaptcha_token: recaptchaToken});
    }

    public verifyLoginEmailOtp(email, otp): Observable<any> {
        return this.http.post<any>(this.backendUrl + 'auth/email/login/verify-otp', {email, otp});
    }

    /*
     * Where the browser is sent to start Google sign-in.
     *
     * A URL rather than a request: this is a full-page departure, because
     * Google's consent screen cannot be loaded by XHR or rendered in a frame.
     * It lives here so the endpoint is assembled in the API layer with the
     * others, not hand-built in a component.
     */
    public get googleRedirectUrl(): string {
        return this.backendUrl + 'auth/google/redirect';
    }

    public verifyCaptcha(captchaResponse): Observable<any> {
        return this.http.post<any>(this.backendUrl + 'auth/verifyCaptcha', {captchaResponse});
    }

    public updateProfile(firstname, lastname, email, company, com_type, job_desc, address, state, pincode, city, phone, fax, user_id): Observable<User> {
        return this.http.post<any>(this.backendUrl + 'auth/updateProfile', {
            firstname,
            lastname,
            email,
            company,
            com_type,
            job_desc,
            address,
            state,
            pincode,
            city,
            phone,
            fax,
            user_id
        });
    }

    public getUserDetail(email): Observable<User> {
        return this.http.post<any>(this.backendUrl + 'auth/getUserDetail', {email});
    }

    public changePassword(email, new_pass, current_pass, encrypted_pass, is_encrypted): Observable<any> {
        return this.http.post<any>(this.backendUrl + 'auth/changePassword', {
            email,
            new_pass,
            current_pass,
            encrypted_pass,
            is_encrypted
        });
    }

    public logout(): Observable<any> {
        return this.http.post<any>(this.backendUrl + 'auth/logout', {});
    }

    public getWishList(customer_id): Observable<WishList[]> {
        return this.http.get<WishList[]>(this.backendUrl + 'wishlist?customer_id=' + customer_id);
    }

    public getWishListById(id): Observable<WishList> {
        return this.http.get<WishList>(this.backendUrl + 'wishlist/' + id);
    }

    public getWishListByToken(token): Observable<WishList> {
        return this.http.get<WishList>(this.backendUrl + 'auth/wishlist/' + token);
    }

    public addToWishListDB(image: string, name: string, customer_id: string) {
        return this.http.post<WishList>(this.backendUrl + 'wishlist', {customer_id, name, image});
    }

    public updateToWishListDB(image: string, id: number, isDelete: boolean) {
        return this.http.put<WishList>(this.backendUrl + 'wishlist', {id, image, isDelete});
    }

    public removeWishList(id: number) {
        return this.http.delete<WishList>(this.backendUrl + 'wishlist?id=' + id);
    }

    public shareLightbox(email: string, id: number, send_email: number) {
        return this.http.post<any>(this.backendUrl + 'share-lightbox', {email, id, send_email});
    }

    public addToCartListDB(image: string) {
        return this.http.post<any>(this.backendUrl + 'cartlist', {image});
    }

    public addContact(name: string, email: string, mobile: string, message: string) {
        return this.http.post<any>(this.backendUrl + 'auth/contact', {name, email, mobile, message});
    }

    public addSubscribe(email: string) {
        return this.http.post<any>(this.backendUrl + 'auth/subscribe', {email});
    }

    public removeCartList() {
        return this.http.delete<CartStatus>(this.backendUrl + 'cartlist');
    }

    public removeCartProduct(image: string) {
        return this.http.post<CartStatus>(this.backendUrl + 'cartProduct', {image});
    }

    public getCartList(customer_id: string) {
        return this.http.get<CartData[]>(this.backendUrl + 'cartlist?customer_id=' + customer_id);
    }

    public getOrders(customer_id: string) {
        return this.http.get<any[]>(this.backendUrl + 'order?customer_id=' + customer_id);
    }

    public getPackageHistory(customer_id: string) {
        return this.http.get<Order[]>(this.backendUrl + 'package-history?customer_id=' + customer_id);
    }

    public getPackageHistoryIpb(pageNumber: number, pageSize: number) {
        const params = {
            mode: 'list',
            is_active: '1',
            package_type: 'Subscription',
            sortBy: 'activated_at',
            sortOrder: 'desc',
            pageNumber: String(pageNumber),
            pageSize: String(pageSize)
        };
        return this.http.get<any>(this.backendUrl + 'package-history-ipb', {params});
    }

    public getOrdersSingle(customer_id: string, type: string, page: number = 1,
                           filters: { from_date?: string; to_date?: string; image_id?: string } = {}) {
        return this.http.get<any>(this.backendUrl + 'orderSingle?customer_id=' + customer_id + '&type=' + type + '&page=' + page
            + '&from_date=' + (filters.from_date || '') + '&to_date=' + (filters.to_date || '')
            + '&image_id=' + (filters.image_id || ''));
    }

    public getSalesDownload(customer_id: string) {
        return this.http.get<any[]>(this.backendUrl + 'sales-download?customer_id=' + customer_id);
    }

    /**
     * Credit pack templates for the pricing page. Answers {data: [...]}, and only
     * the packs with status = 1 and ip_pack_status = 1.
     */
    public getPlanDetail() {
        return this.http.get<{ data: CreditPack[] } | CreditPack[]>(this.backendUrl + 'auth/plan');
    }

    public forgotPassword(email: string) {
        return this.http.get<any>(this.backendUrl + 'auth/resetPassword?email=' + email);
    }

    public fetchSettings(): Observable<any> {
        return this.http.get(this.siteadminUrl + 'setting');
    }


    public downloadImage(imageData: CartData, type: boolean): Observable<any> {
        return this.http.post<any>(this.backendUrl + 'download', {imageData, type});
    }

    public reDownloadImage(id: number): Observable<string> {
        return this.http.post<string>(this.backendUrl + 'reDownloadImage', {id});
    }

    public reDownloadImageIpb(id: number): Observable<any> {
        return this.http.post<any>(this.backendUrl + 'reDownloadImageIpb', {id});
    }

    public reDownloadImageById(imageId: string, size: string): Observable<string> {
        return this.http.post<string>(this.backendUrl + 'reDownloadImageById', {imageId, size});
    }

    public downloadVideo(imageData: CartData, type: boolean): Observable<Subscription[]> {
        return this.http.post<Subscription[]>(this.backendUrl + 'download-video', {imageData, type});
    }

    public downloadSalesImage(imageData: CartData, type: boolean): Observable<Subscription[]> {
        return this.http.post<Subscription[]>(this.backendUrl + 'download-sales-image', {imageData});
    }

    public downloadSalesVideo(imageData: CartData): Observable<Subscription[]> {
        return this.http.post<Subscription[]>(this.backendUrl + 'download-sales-video', {imageData});
    }

    public compDownload(imageData: CartData): Observable<any> {
        return this.http.post<any>(this.backendUrl + 'comp-download', {imageData});
    }

    public getOrderImages(orderId: number): Observable<any> {
        return this.http.post<any>(this.backendUrl + 'receiptImages', {orderId});
    }

    public getContributorData(text, collection): Observable<ContributorData[]> {
        return this.http.get<ContributorData[]>(this.contributorUrl + '/contributor-data.php?contributor_code=' + text + '&collection=' + collection);
    }

    public addPageView(page_url: string, is_plan_page: number) {
        return this.http.post<any>(this.backendUrl + 'auth/page-view', {page_url, is_plan_page});
    }

    public getAccountDashboardData() {
        // skipLoader: the header's credit pill is background data, not a page load.
        return this.http.get<any>(this.backendUrl + 'accountDashboardData', { params: { skipLoader: 'true' } });
    }

    /**
     * Flush the backend's derived caches — compiled config, routes, views, the
     * class loader and the application cache. See CacheAdmin on the backend.
     *
     * Always answers 200; a command that failed is reported inside the body, so
     * read `failed` and `results` rather than relying on the HTTP status.
     */
    public clearBackendCache() {
        /*
         * POST, not GET: this changes server state, and a GET would be fair game
         * for a prefetcher or a proxy to fire on its own.
         *
         * skipLoader is not set on purpose — the caller wants the spinner, since
         * dropping the compiled config makes the next request genuinely slow.
         */
        return this.http.post<any>(this.backendUrl + 'auth/clear-cache', {});
    }

    public getImageById(id, collection, searchText): Observable<Results> {
        return this.http.get<Results>(this.backendUrl + 'auth/singleImageDetail?imageId=' + id + '&collection=' + collection + '&searchText=' + searchText);
    }

    public getVideoById(id, collection, searchText): Observable<Results> {
        return this.http.get<Results>(this.backendUrl + 'auth/singleVideoDetail?' + 'item=' + id + '&collection=' + collection + '&searchText=' + searchText);
    }

    public getSubscriptionDetail(category: string, collection: string, image_id: string, license: string) {
        let options: any = {};
        const skipLoader = true;
        if (skipLoader) {
            options = {
                params: { skipLoader },
                headers: { ignoreLoadingBar: skipLoader }
            };
        }
        return this.http.post<Subscription[]>(this.backendUrl + 'subscription-detail', {
            category,
            collection,
            image_id,
            license
        }, options);
    }

    public getVideoSubscriptionDetail(category: string, collection: string, video_id: string, license: string) {
        return this.http.post<Subscription[]>(this.backendUrl + 'download-video-detail', {
            category,
            collection,
            video_id,
            license
        });
    }

    public encryptdataCashfree(request, customers_id, promo_id) {
        // const url = 'http://localhost:3000/api/orders/encryptFormData';
        const url = this.backendUrl + 'encryptFormDataCashfree';
        const data = {
            request,
            customers_id,
            promo_id
        };
        return this.http.get<any>(url, {params: data});
        // return this.http.get<string>(this.backendUrl + 'encryptFormData?request=' + request);
    }

    public encryptPlandata(request, plan_id, promo_id) {
        // const url = 'http://localhost:3000/api/orders/encryptFormData';
        const url = this.backendUrl + 'encryptPlanData';
        const data = {
            request,
            plan_id,
            promo_id
        };
        return this.http.get<any>(url, {params: data});
        // return this.http.get<string>(this.backendUrl + 'encryptFormData?request=' + request);
    }

    public getPlan(id): Observable<Plan> {
        return this.http.get<Plan>(this.backendUrl + 'plan/' + id);
    }


    public addQueryForm(element: Contact) {
        return this.http.post<any>(this.backendUrl + 'auth/query-form', element);
    }

    public getCollections(): Observable<any> {
        return this.http.get(this.siteadminUrl + 'collections-ip');
    }

    public getCollectionsIPAndIPB(): Observable<any> {
        return this.http.get(this.siteadminUrl + 'collections-ip-ipb');
    }

    public getCollectionById($id): Observable<any> {
        return this.http.get(this.siteadminUrl + 'collection-id/' + $id);
    }

    public getImagePrice($name): Observable<any> {
        return this.http.get(this.siteadminUrl + 'image-price/' + $name);
    }


    public applyPromo(promo: string, type: string) {
        const httpOptions = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                Accept: 'application/json, text/plain'
            })
        };
        return this.http.post<any>(this.backendUrl + 'auth/applyPromo', {promo, type}, httpOptions);
    }

    public addAiEnquiry(formData: FormData) {
        return  this.http.post(this.backendUrl + 'auth/ai-enquiry', formData);
    }

    public submitCustomPlanEnquiry(data: any) {
        return this.http.post(this.backendUrl + 'auth/custom-plan-enquiry', data);
    }

    generateImage(prompt: string, aspect: string, medium: string, customer_id: string, customer_from: number): Observable<any> {
        // console.log('API call - generateImage with customer_from:', customer_id);
        return this.http.post(this.backendUrl + 'ai-image-generate', {
            prompt,
            aspect_ratio: aspect,
            medium,
            customer_id,
            customer_from
        });
    }

    getAIImages(uuid: string): Observable<any> {
        let options = {};
        const skipLoader = true;
        if (skipLoader) {
            options = {
                params: {skipLoader},
                headers: {ignoreLoadingBar: skipLoader}
            };
        }
        return this.http.get(this.backendUrl + 'ai-image/' + uuid, options);
    }

    getAIImageDetail(uuid: string): Observable<any> {
        let options = {};
        const skipLoader = true;
        if (skipLoader) {
            options = {
                params: {skipLoader},
                headers: {ignoreLoadingBar: skipLoader}
            };
        }
        return this.http.get(this.backendUrl + 'ai-image/detail/' + uuid, options);
    }

    public getSubscriptionDetailAI() {
        return this.http.post<Subscription[]>(this.backendUrl + 'subscription-detail-ai', {});
    }

    public getPromptHistory(customer_id: string, page: number = 1) {
        return this.http.get<any>(this.backendUrl + 'prompt-history?customer_id=' + customer_id + '&page=' + page);
    }
    public getPromptHistoryNanoBanana(customer_id: string, page: number = 1) {
        return this.http.get<any>(this.backendUrl + 'prompt-history-nano-banana?customer_id=' + customer_id + '&page=' + page);
    }
    public getPromptHistoryRecent(customer_id: string) {
        return this.http.get<any>(this.backendUrl + 'prompt-history-recent?customer_id=' + customer_id);
    }

    public downloadAIImage(imageData: CartData, type: boolean): Observable<Subscription> {
        return this.http.post<Subscription>(this.backendUrl + 'download-ai', {imageData, type});
    }

    public getRecentImagesGenerated() {
        return this.http.post<any>(this.backendUrl + 'recent-images-generated', {});
    }

    public downloadLicenseImage(payload: any) {
        return this.http.post<any>(this.backendUrl + 'ai/license-image', payload);
    }

    public requestLicenseAIImage(payload: any): Observable<any> {
        return this.http.post<any>(this.backendUrl + 'ai/license-image-request', payload);
    }

    public verifyLicenseToken(payload: any): Observable<any> {
        return this.http.post<any>(this.backendUrl + 'license/verify-token', payload);
    }

    public processLicenseAction(payload: any): Observable<any> {
        return this.http.post<any>(this.backendUrl + 'license/process', payload);
    }



    //Nano banana API
    generateNanoBanana(data: FormData): Observable<any> {
        const headers = new HttpHeaders(); // empty
        return this.http.post(this.backendUrl + 'generate-image', data, { headers });
    }


    public getAiStatus(aiModel: string) {
        return this.http.get<any>(this.backendUrl + 'ai/status?ai_model=' + aiModel);
    }

    public updateAiStatus(aiModel: string, status: number) {
        return this.http.post<any>(this.backendUrl + 'ai/status',
            {
                ai_model: aiModel,
                status: status
            }
        );
    }


    //Nano Downlaod
    public downloadNanoCompImage(imageData: any, type: boolean): Observable<any> {
        return this.http.post<any>(
            this.backendUrl + 'download-nano-comp',
            { imageData, type }
        );
    }

    public downloadNanoHighResImage(imageData: any, type: boolean): Observable<any> {
        return this.http.post<any>(
            this.backendUrl + 'download-nano-high',
               { imageData, type, customer_from: imageData.customer_from, customer_id: imageData.customer_id }
        );
    }

    public reDownloadNanoHighResImage(imageData: any, type: boolean): Observable<any> {
        return this.http.post<any>(
            this.backendUrl + 're-download-nano-high',
            { imageData, type }
        );
    }

    public editingTool(imageUuid: string, tool: string, maskBase64:string, prompt:string='', customer_id: string, customer_from: any): Observable<any> {
        return this.http.post<any>(
            this.backendUrl + 'edit-image',
            {
                image_uuid: imageUuid,
                tool: tool,
                maskBase64: maskBase64,
                prompt: prompt,
                customer_id: customer_id,
                customer_from: customer_from
            }
        );
    }

    public upscaleImage(data: FormData): Observable<any> {
        return this.http.post<any>(
            this.backendUrl + 'upscale-image',data);
    }

    // Returns active AI permission rows for a customer (image-gen + tool access).
    public getCustomerAiPermission(customerId: any): Observable<any> {
        return this.http.get<any>(this.backendUrl + 'ai-features/customer-permission?customer_id=' + customerId);
    }


}
